/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ActionButton } from '@fluentui/react/lib/Button';
import { IColumn, IDetailsHeaderProps, IDetailsRowProps } from '@fluentui/react/lib/DetailsList';
import { IStackProps, IStackSlots, Stack, StackItem } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/utilities';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import { CluEntityRegexInEditMode } from 'src/clu/appShell/cluEntityDetailsPage/components/CluEntityRegexInEditMode';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { Table } from 'src/core/components/Table';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType, TableColumnProps, TableProps } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { RegexEntityCapability } from 'src/core/types/EntityStore';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';

export type CluEntityRegexCapabilityTableProps = TableProps<RegexEntityCapability> & {
    dismissEditingRegex: () => void;
    regexBeingEdited: RegexEntityCapability;
    onEditRegex: (regex: RegexEntityCapability) => void;
    onAddRegex: (regex: RegexEntityCapability) => void;
};

const messages = defineMessages({
    regexKey: { id: 'clu.cluEntityRegexCapabilityTableProps.regexKey', defaultMessage: 'Regex key' },
    expression: { id: 'clu.cluEntityRegexCapabilityTableProps.expression', defaultMessage: 'Expression' },
    addExpression: { id: 'clu.cluEntityRegexCapabilityTableProps.addExpression', defaultMessage: 'Add expression' },
    language: { id: 'clu.cluEntityRegexCapabilityTableProps.language', defaultMessage: 'Language' }
});

const scopedTheme = getScopedTheme('CluEntityRegexCapabilityTable');
const rowStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { borderBottom: `${scopedTheme.borderColor} solid 1px`, height: '42px', paddingLeft: '52px' }
});

/**
 * A component displaying all regex in an entity's regex capability.
 */
export const CluEntityRegexCapabilityTable = observer((props: CluEntityRegexCapabilityTableProps) => {
    const { items, regexBeingEdited, selection, dismissEditingRegex, onEditRegex, onAddRegex } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const { projectType } = rootStore.currentCluProjectStore;

    const [isAddingNewRegex, setIsAddingNewRegex] = useState<boolean>();
    const [sortKey, setSortKey] = React.useState<SortKeyType<RegexEntityCapability>>({ key: 'regexKey', order: 'asc' });
    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(
        orderBy(items, [createKeyedStringNormalizer(sortKey?.key)], [sortKey?.order]),
        5
    );

    const isMultiLingualProject = rootStore.currentCluProjectStore.isMultiLingual;

    const onRenderTableHeader = (headerProps: IDetailsHeaderProps, defaultRenderer: (props?: IDetailsHeaderProps) => JSX.Element) => (
        <Stack>
            {defaultRenderer(headerProps)}

            <StackItem styles={rowStyles}>
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    hidden={canWrite || isAddingNewRegex}
                    data-automation-id="addNewRegexButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <ActionButton
                            iconProps={{ iconName: 'Add' }}
                            text={localize(messages.addExpression)}
                            disabled={!canWrite || isAddingNewRegex}
                            onClick={() => setIsAddingNewRegex(true)}
                        />
                    }
                />
            </StackItem>

            {isAddingNewRegex && (
                <CluEntityRegexInEditMode
                    projectType={projectType}
                    columns={headerProps.columns}
                    onDismiss={() => setIsAddingNewRegex(false)}
                    projectCulture={rootStore.currentCluProjectStore.culture}
                    onSubmit={regex => {
                        onAddRegex(regex);
                        setIsAddingNewRegex(false);
                    }}
                />
            )}
        </Stack>
    );

    const onRenderTableRow = (rowProps: IDetailsRowProps, defaultRenderer: (props?: IDetailsRowProps) => JSX.Element) => {
        const isInEditMode = (rowProps.item as RegexEntityCapability).id === regexBeingEdited?.id;

        return isInEditMode ? (
            <CluEntityRegexInEditMode
                rowProps={rowProps}
                onSubmit={onEditRegex}
                projectType={projectType}
                columns={rowProps.columns}
                onDismiss={dismissEditingRegex}
                projectCulture={rootStore.currentCluProjectStore.culture}
                initialRegex={rowProps.item as RegexEntityCapability}
            />
        ) : (
            defaultRenderer({ ...rowProps, styles: mergeStyleSets(rowProps.styles, { cell: { alignSelf: 'center', fontSize: '14px' } }) })
        );
    };

    const onSortColumn = (column: IColumn) => {
        setSortKey({ key: column.key as keyof RegexEntityCapability, order: column?.isSortedDescending ? 'asc' : 'desc' });
        selection.setAllSelected(false);
    };

    const columns: TableColumnProps[] = [
        {
            minWidth: 100,
            maxWidth: 150,
            key: 'regexKey',
            isRowHeader: true,
            isResizable: true,
            fieldName: 'regexKey',
            isCollapsible: false,
            name: localize(messages.regexKey),
            isSorted: sortKey?.key === 'regexKey',
            isSortedDescending: sortKey?.key === 'regexKey' && sortKey?.order === 'desc'
        },
        ...(isMultiLingualProject
            ? [
                  {
                      minWidth: 100,
                      maxWidth: 150,
                      key: 'language',
                      isResizable: true,
                      isSortable: false,
                      isRowHeader: false,
                      isCollapsible: false,
                      fieldName: 'language',
                      name: localize(messages.language),
                      onRender: (item: RegexEntityCapability) => (
                          <Stack tokens={{ childrenGap: 12 }}>
                              <LuisText>
                                  {
                                      (projectType === 'Conversation'
                                          ? rootStore.cluConversationCultureStoreHouse
                                          : rootStore.cluOrchestrationCultureStoreHouse
                                      ).items.find(l => l.code === item.language)?.name
                                  }
                              </LuisText>
                          </Stack>
                      )
                  }
              ]
            : []),
        {
            minWidth: 150,
            maxWidth: 200,
            key: 'expression',
            fieldName: 'expression',
            isResizable: true,
            isSortable: false,
            isRowHeader: false,
            isCollapsible: true,
            name: localize(messages.expression)
        }
    ];

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
        selection.setAllSelected(false);
    };

    return (
        <Stack>
            <Table<RegexEntityCapability>
                {...props}
                items={pageItems}
                columns={columns}
                onSortColumn={onSortColumn}
                onRenderRow={onRenderTableRow}
                onRenderDetailsHeader={onRenderTableHeader}
            />

            <TablePagination
                pageCount={pageCount}
                currentPage={currentPage}
                onPageChange={p => handlePageChange(p.selected)}
                containerStyles={{ root: { marginTop: '0 !important' } }}
            />
        </Stack>
    );
});
